import { useMemo, useState } from "react";
import { normalizeSearchText } from "@/utils/searchText";

/**
 * Search + dynamic filtering for the articles and regulations indexes,
 * matching what the organizations and professionals directories already
 * do: a free-text search across the item's own fields, plus select
 * options DERIVED FROM THE LOADED DATA rather than a hardcoded list — so
 * a filter only ever offers values that will actually match something.
 */

/** Default threshold for the content indexes: below this many entries
 *  the filter selects are more noise than help. Pass 0 to disable the
 *  threshold entirely. */
export const INDEX_FILTER_MIN = 15;

export type IndexViewMode = "grid" | "list";

/** One derived filter: where its values come from and how it's labelled. */
export interface FilterFieldSpec<T> {
    name: string;
    /** Every value this item contributes (arrays are flattened). */
    getValues: (item: T) => Array<string | undefined | null>;
}

export interface UseIndexFilteringResult<T> {
    searchQuery: string;
    setSearchQuery: (v: string) => void;
    viewMode: IndexViewMode;
    setViewMode: (m: IndexViewMode) => void;
    /** Distinct, sorted options per filter — empty when nothing qualifies. */
    options: string[][];
    /** Selected value per filter ("" = All). */
    selected: string[];
    setSelectedAt: (index: number, value: string) => void;
    /** Items after search + every active filter. */
    filtered: T[];
    /** True when any filter or the search box is active. */
    isFiltering: boolean;
    /** Whether the dataset is large enough for filters to be offered. */
    filtersEnabled: boolean;
    clearAll: () => void;
}

function distinctSorted(values: Array<string | undefined | null>): string[] {
    const seen = new Map<string, string>();
    for (const raw of values) {
        const value = String(raw ?? "").trim();
        if (!value) continue;
        const key = value.toLowerCase();
        if (!seen.has(key)) seen.set(key, value);
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

export function useIndexFiltering<T>(
    items: T[],
    filters: Array<FilterFieldSpec<T>>,
    /** Fields the free-text search scans. */
    getSearchableText: (item: T) => Array<string | undefined | null>,
    // List is the default presentation everywhere (owner preference);
    // pages can still opt into grid-first explicitly.
    initialViewMode: IndexViewMode = "list",
    /**
     * Minimum dataset size before filters apply — must match the value
     * given to FilterBar. Enforced here as well so a selection made
     * before the dataset shrank can't keep filtering invisibly once the
     * selects are hidden. 0 = always active.
     */
    filterMin = 0,
): UseIndexFilteringResult<T> {
    const [searchQuery, setSearchQuery] = useState("");
    const [viewMode, setViewMode] = useState<IndexViewMode>(initialViewMode);
    const [selected, setSelected] = useState<string[]>(() =>
        filters.map(() => ""),
    );

    const setSelectedAt = (index: number, value: string) =>
        setSelected((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });

    // Options come from the data, so an empty database yields an empty
    // select rather than a list of choices that match nothing.
    const filtersEnabled = filterMin <= 0 || items.length >= filterMin;

    const options = useMemo(
        () =>
            filtersEnabled
                ? filters.map((spec) =>
                      distinctSorted(
                          items.flatMap((item) => spec.getValues(item)),
                      ),
                  )
                : filters.map(() => []),
        [items, filters, filtersEnabled],
    );

    const filtered = useMemo(() => {
        const needle = normalizeSearchText(searchQuery.trim());
        // Normalize each active filter's target ONCE rather than per item
        // per candidate value — this runs on every keystroke.
        const active = filtersEnabled
            ? filters
                  .map((spec, i) => ({
                      spec,
                      want: String(selected[i] ?? "").trim().toLowerCase(),
                  }))
                  .filter((f) => f.want.length > 0)
            : [];

        if (!needle && active.length === 0) return items;

        return items.filter((item) => {
            if (needle) {
                const haystack = normalizeSearchText(
                    getSearchableText(item).filter(Boolean).join(" "),
                );
                if (!haystack.includes(needle)) return false;
            }
            for (const { spec, want } of active) {
                const has = spec
                    .getValues(item)
                    .some(
                        (v) =>
                            String(v ?? "")
                                .trim()
                                .toLowerCase() === want,
                    );
                if (!has) return false;
            }
            return true;
        });
    }, [items, filters, selected, searchQuery, getSearchableText, filtersEnabled]);

    const isFiltering =
        searchQuery.trim().length > 0 ||
        (filtersEnabled && selected.some((v) => !!v));

    const clearAll = () => {
        setSearchQuery("");
        setSelected(filters.map(() => ""));
    };

    return {
        filtersEnabled,
        searchQuery,
        setSearchQuery,
        viewMode,
        setViewMode,
        options,
        selected,
        setSelectedAt,
        filtered,
        isFiltering,
        clearAll,
    };
}
